import { DuplicateBanner } from '@components'

import { Attachment, DrawerContextProvider, DrawerMode, Flex, ModuleLayout, Text, useModal } from '@billy/design-system'
import { ChangeEvent, ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { useUpdateEffect } from 'react-use'

import { AttachmentList } from '@features/attachments/elements/AttachmentList'
import { DuplicatedReceiptsModal } from '@features/attachments/elements/DuplicatedReceiptsModal'
import { AttachmentFilterType } from '@features/attachments/enums/attachmentFiltertype'
import { AttachmentType } from '@features/attachments/enums/attachmentType'
import { useAttachmentsCounter } from '@features/attachments/hooks/useAttachmentsCounter'
import { useIsEInvoicingOnly } from '@features/bills/hooks/useIsEInvoicingOnly'
import { EInvoicingSetupContextProvider } from '@features/eInvoicing/context/EInvoicingSetupContext'
import { EInvoicingDashboardBanner } from '@features/eInvoicing/elements/EinvoicingDashboardBanner'
import { EInvoicingEnrollmentInformationBanner } from '@features/eInvoicing/elements/EInvoicingEnrollmentInformationBanner'
import { AccountsContextProvider } from '@modules-deprecated/app/accounts'
import { useUserOrganization, useUserOrganizationSettings } from '@modules-deprecated/app/organization'
import { Organization } from '@modules-deprecated/app/organization/types/organization'
import { ExpensesReceiptUploadSources, ExpensesTrackingEvents } from '@views/receipts/enums/tracking'
import { useReceiptsTabs } from '@views/receipts/hooks/useReceiptsTabs'

import { EmberRoute } from '../../../../enums/emberRoute'
import { ModalId } from '../../../../enums/modalId'
import { TrackingContext } from '../../../../enums/trackingContext'
import { useSegment } from '../../../../hooks'
import { useEmberRouteUrl } from '../../../../hooks/routing/useEmberRouteUrl'
import { useOrgUsesReceiptsOnly } from '../../../../hooks/useOrgUsesReceiptsOnly'
import { getOrganizationViewUrl } from '../../../../utils/routing/getOrganizationViewUrl'
import { reactRoute } from '../../../../utils/routing/reactRoute'
import { ReceiptUploadGateProvider } from '../../contexts/ReceiptUploadGateContext'
import { AutoForwardSimplificationBanner } from '../../elements/AutoForwardSimplificationBanner'
import { ReceiptButton } from '../../elements/ReceiptButton'
import { ReceiptDropzoneFullScreen } from '../../elements/ReceiptDropzoneFullScreen'
import { ReceiptsActions } from '../../elements/ReceiptsActions'
import { ReceiptsEmptyState } from '../../elements/ReceiptsEmptyState'
import { ReceiptsListSideActions } from '../../elements/ReceiptsListSideActions'
import { ReceiptsPagination } from '../../elements/ReceiptsPagination'
import { ReceiptsSidePanel } from '../../elements/ReceiptsSidePanel'
import { ReceiptTransferStatusBadge } from '../../elements/ReceiptTransferStatusBadge'
import { ScoutBanner } from '../../elements/ScoutBanner'
import { ScoutConnectionErrorModal } from '../../elements/ScoutConnectionErrorModal'
import { ScoutStartDatePicker } from '../../elements/ScoutStartDatePicker'
import { UploadsTabs } from '../../elements/UploadsTabs'
import { useAttachments } from '../../hooks/useAttachments'
import { defaultAttachmentsQuery, useAttachmentsFilters } from '../../hooks/useAttachmentsFilters'
import { useDuplicateAttachments } from '../../hooks/useDuplicateAttachments'
import { useFetchScoutConnections } from '../../hooks/useFetchScoutConnections'
import { useScoutAccess } from '../../hooks/useScoutAccess'
import { useScoutConnectionId } from '../../hooks/useScoutConnectionId'
import { getReceiptOwnerIntegrationStatuses } from '../../utils/getReceiptOwnerIntegrationStatuses'
import { EInvoicingTabViewTracker } from './elements/EInvoicingTabViewTracker'
import * as Styled from './styles'

export const ReceiptsListComponent = ({ organization }: { organization: Organization }): ReactElement => {
  const [selectedReceiptsIds, setSelectedReceiptsIds] = useState<string[]>([])
  const receiptsWrapperRef = useRef<HTMLDivElement>(null)

  const isEInvoicingOnly = useIsEInvoicingOnly()
  const orgUsesReceiptsOnly = useOrgUsesReceiptsOnly()

  const { t } = useTranslation()
  const navigate = useNavigate()
  const { track } = useSegment()
  const { organizationSettings, isLoading: areOrganizationSettingsLoading } = useUserOrganizationSettings()
  const { url: vouchersUrl } = useEmberRouteUrl(EmberRoute.Vouchers)
  const { duplicates } = useDuplicateAttachments()
  const {
    hasReceivedEInvoices,
    isLoading: isEInvoiceCountLoading,
    countMap,
  } = useAttachmentsCounter({
    enabled: !orgUsesReceiptsOnly,
  })
  const tabs = useReceiptsTabs({
    hasReceivedEInvoices: orgUsesReceiptsOnly ? true : hasReceivedEInvoices,
  })
  const [firstTab] = tabs
  const [filters, setFilters] = useAttachmentsFilters({
    defaultType: firstTab.type,
    setDefaultQuery: true,
  })
  const { attachments: receiptsList, isLoading } = useAttachments(filters)

  const { open: openScoutConnectionErrorModal } = useModal(ModalId.ScoutConnectionErrorModal)
  const { open: openDuplicateAttachmentsModal } = useModal(ModalId.DuplicatedReceiptsActionModal)
  const [selectedDuplicates, setSelectedDuplicates] = useState<Attachment[] | null>(null)

  const tabCountMap = orgUsesReceiptsOnly ? {} : countMap
  const tabCountLoading = orgUsesReceiptsOnly ? false : isEInvoiceCountLoading

  const handleOpenDuplicate = useCallback(
    (attachmentId: string) => {
      const matched = duplicates.find((d) => d.id === attachmentId)
      setSelectedDuplicates(matched ? [matched] : null)
      openDuplicateAttachmentsModal()
    },
    [duplicates, openDuplicateAttachmentsModal],
  )

  const handleDuplicateReviewClick = useCallback(() => {
    track(ExpensesTrackingEvents.DuplicateReviewClicked)
    setSelectedDuplicates(null)
    openDuplicateAttachmentsModal()
  }, [openDuplicateAttachmentsModal, track])

  const { scoutConnectionId, error: scoutConnectionIdError } = useScoutConnectionId()
  const { connections: scoutConnections } = useFetchScoutConnections()
  const hasScoutConnections = !!scoutConnections?.length
  const isInboxEnabled = organizationSettings?.isInboxEnabled === '1'
  const isEmpty = !isLoading && !receiptsList.length
  const isSearching = !!filters.searchQuery
  const isEInvoicing = filters.type === 'EDocument'
  const isEmptyEInvoiceTab = !orgUsesReceiptsOnly && isEInvoicing && !isEInvoiceCountLoading && !hasReceivedEInvoices
  const showScoutConnectionDatePicker = filters.type === AttachmentFilterType.Scout && scoutConnectionId
  const showActions = (!isEmpty && !isEmptyEInvoiceTab) || isSearching
  const showNoSearchResults = isSearching && isEmpty
  const showEmptyState = (isEmpty || isEmptyEInvoiceTab) && !isSearching

  const { shouldShowScout } = useScoutAccess()
  const shouldShowScoutBanner = shouldShowScout && !hasScoutConnections && !isEInvoicingOnly

  useEffect(() => {
    const isVouchersInboxEnabled = !areOrganizationSettingsLoading && isInboxEnabled && vouchersUrl

    if (isVouchersInboxEnabled) {
      navigate(vouchersUrl)
    }
  }, [areOrganizationSettingsLoading, navigate, isInboxEnabled, vouchersUrl])

  useEffect(() => {
    if (scoutConnectionIdError === 'UserEmailNotFound') {
      openScoutConnectionErrorModal()
    }
  }, [scoutConnectionIdError, openScoutConnectionErrorModal])

  // Remove selection when filters or settings are changed
  useUpdateEffect(() => {
    setSelectedReceiptsIds([])
  }, [filters])

  const hasSelectedScoutReceipts = useMemo(() => {
    return selectedReceiptsIds.some(
      (id) => receiptsList.find((receipt) => receipt.id === id)?.type === AttachmentType.Scout,
    )
  }, [receiptsList, selectedReceiptsIds])

  const handleSelectAllAction = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (event.target.checked) {
        setSelectedReceiptsIds(receiptsList.map(({ id }) => id))
      } else {
        setSelectedReceiptsIds([])
      }
    },
    [receiptsList],
  )

  const handleReceiptSelect = useCallback((id: string, isSelected: boolean) => {
    if (isSelected) {
      setSelectedReceiptsIds((prevSelectedReceiptsIds) => [...prevSelectedReceiptsIds, id])
    } else {
      setSelectedReceiptsIds((prevSelectedReceiptsIds) =>
        prevSelectedReceiptsIds.filter((selectedId) => selectedId !== id),
      )
    }
  }, [])

  const handleFileSelect = useCallback(
    (attachmentId: string) => {
      const newFromAttachmentRoute = reactRoute.organization.bills.getNewBillFromAttachmentRoute(attachmentId)
      const billsNewFromAttachmentUrl = getOrganizationViewUrl(organization?.url, newFromAttachmentRoute)
      navigate(billsNewFromAttachmentUrl)
    },
    [navigate, organization?.url],
  )

  const handleTabClick = useCallback(
    (type: AttachmentFilterType) => () => {
      setFilters({ type, page: defaultAttachmentsQuery.page })
    },
    [setFilters],
  )

  const handleOnDrawerModeChange = useCallback(
    (drawerMode: DrawerMode) => {
      setFilters({ drawerMode })
    },
    [setFilters],
  )

  const title = isEInvoicingOnly ? t('receipts.receipts_list.type.e_invoice') : t('receipts.title')
  return (
    <ReceiptUploadGateProvider>
      <DrawerContextProvider drawerMode={filters.drawerMode} onDrawerModeChange={handleOnDrawerModeChange}>
        <EInvoicingSetupContextProvider>
          <EInvoicingTabViewTracker isEInvoicing={isEInvoicing} />
          <ModuleLayout
            sideActions={
              <ReceiptsListSideActions
                hasSelectedScoutReceipts={hasSelectedScoutReceipts}
                selectedReceiptsIds={selectedReceiptsIds}
                setSelectedReceiptsIds={setSelectedReceiptsIds}
              />
            }
            title={title}
          >
            <AutoForwardSimplificationBanner />
            {duplicates.length > 0 && <DuplicateBanner onPress={handleDuplicateReviewClick} />}
            {shouldShowScoutBanner && <ScoutBanner />}
            {showScoutConnectionDatePicker ? (
              <>
                {!isEInvoicing ? <EInvoicingDashboardBanner source="receipts-list" /> : null}
                <Flex>
                  <UploadsTabs
                    tabs={tabs}
                    selectedTab={filters.type}
                    handleTabClick={handleTabClick}
                    countMap={tabCountMap}
                    isCountLoading={tabCountLoading}
                  />
                </Flex>
                <Styled.ReceiptsWrapper ref={receiptsWrapperRef}>
                  <ScoutStartDatePicker />
                </Styled.ReceiptsWrapper>
              </>
            ) : (
              <>
                {!isEInvoicing ? <EInvoicingDashboardBanner source="receipts-list" /> : null}
                <Flex justifyContent="space-between">
                  <UploadsTabs
                    tabs={tabs}
                    selectedTab={filters.type}
                    handleTabClick={handleTabClick}
                    countMap={tabCountMap}
                    isCountLoading={tabCountLoading}
                  />
                  {showActions && (
                    <ReceiptsActions
                      hideSelectAll={orgUsesReceiptsOnly}
                      onSelectAllActionChange={handleSelectAllAction}
                      selectedReceiptsCount={selectedReceiptsIds.length}
                    />
                  )}
                </Flex>
                {isEInvoicing && !showEmptyState ? <EInvoicingEnrollmentInformationBanner /> : null}
                <Styled.ReceiptsWrapper ref={receiptsWrapperRef}>
                  {showEmptyState && <ReceiptsEmptyState type={filters.type} />}
                  {!isEmpty && !isEmptyEInvoiceTab && (
                    <>
                      <AttachmentList isLoading={isLoading} shouldShowAddNewTile={false}>
                        {receiptsList.map((receipt) => {
                          const handleSelectClick = () => handleFileSelect(receipt.id)

                          const footer =
                            isEInvoicingOnly || orgUsesReceiptsOnly ? undefined : <ReceiptButton attachment={receipt} />
                          return (
                            <AttachmentList.Tile
                              key={receipt.id}
                              attachment={receipt}
                              footer={footer}
                              isSelectable={orgUsesReceiptsOnly ? false : undefined}
                              isSelected={selectedReceiptsIds.includes(receipt.id)}
                              isShoeboxReadOnly={orgUsesReceiptsOnly}
                              onSelect={orgUsesReceiptsOnly ? undefined : handleReceiptSelect}
                              onFileSelectFromPreview={orgUsesReceiptsOnly ? undefined : handleSelectClick}
                              onOpenDuplicate={handleOpenDuplicate}
                              thumbnailBadge={
                                orgUsesReceiptsOnly ? (
                                  <ReceiptTransferStatusBadge
                                    integrationStatuses={getReceiptOwnerIntegrationStatuses(receipt)}
                                  />
                                ) : undefined
                              }
                            />
                          )
                        })}
                      </AttachmentList>
                      <ReceiptsPagination listRef={receiptsWrapperRef} isLoading={isLoading} />
                    </>
                  )}
                  {showNoSearchResults && <Text alignment="center">{t('search.no_results')}</Text>}
                </Styled.ReceiptsWrapper>
                {!isEmpty && !isEmptyEInvoiceTab && !isEInvoicingOnly && (
                  <ReceiptDropzoneFullScreen
                    trackingContext={TrackingContext.Uploads}
                    receiptUploadSource={ExpensesReceiptUploadSources.ReceiptFullScreenDragAndDrop}
                  />
                )}
              </>
            )}
            <AccountsContextProvider organizationId={organization?.id}>
              <ReceiptsSidePanel />
            </AccountsContextProvider>
            <ScoutConnectionErrorModal />
            <DuplicatedReceiptsModal
              id={ModalId.DuplicatedReceiptsActionModal}
              duplicates={selectedDuplicates ?? duplicates}
            />
          </ModuleLayout>
        </EInvoicingSetupContextProvider>
      </DrawerContextProvider>
    </ReceiptUploadGateProvider>
  )
}

export const ReceiptsList = () => {
  const { organization } = useUserOrganization()
  if (!organization) {
    return null
  }

  return <ReceiptsListComponent organization={organization} />
}
